/**
 * Shared holiday calendar used by timesheet locking and admin management.
 * Populated from /api/holidays (HolidayProvider); falls back to empty until loaded.
 */

/** @type {Array<{ id?: string, date: string, title: string, type?: string, isOptional?: boolean }>} */
let holidaysCache = [];

/**
 * Normalize DB/API date values to YYYY-MM-DD.
 * @param {unknown} value
 * @returns {string|null}
 */
export function normalizeHolidayDate(value) {
  if (value == null) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    return value.trim().slice(0, 10);
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return null;
}

/**
 * Optional holidays must not lock timesheet entry — employees pick Holiday manually.
 * @param {{ type?: string, isOptional?: boolean, is_optional?: boolean }} holiday
 * @returns {boolean}
 */
export function isOptionalHoliday(holiday) {
  if (!holiday) return false;
  if (holiday.isOptional === true || holiday.is_optional === true) return true;
  return String(holiday.type || '').toLowerCase() === 'optional';
}

/**
 * @param {Array<object>} holidays
 */
export function setHolidaysCache(holidays) {
  holidaysCache = Array.isArray(holidays)
    ? holidays
        .map((h) => {
          const date = normalizeHolidayDate(h.date);
          if (!date || !h.title) return null;
          return {
            ...h,
            date,
            title: String(h.title),
            isOptional: isOptionalHoliday(h),
          };
        })
        .filter(Boolean)
    : [];
}

export function getHolidaysCache() {
  return holidaysCache;
}

/**
 * Returns a system holiday that should auto-lock the day (non-optional only).
 * @param {string} date YYYY-MM-DD
 * @returns {object|undefined}
 */
export function getHolidayByDate(date) {
  const key = normalizeHolidayDate(date);
  if (!key) return undefined;
  return holidaysCache.find((h) => h.date === key && !isOptionalHoliday(h));
}

/**
 * Any holiday row for the date (including optional) — for admin display helpers.
 * @param {string} date
 * @returns {object|undefined}
 */
export function getAnyHolidayByDate(date) {
  const key = normalizeHolidayDate(date);
  if (!key) return undefined;
  return holidaysCache.find((h) => h.date === key);
}
